export enum UserRole {
    Clinical = "Clinical",
    Material = "Materials",
    NotSet = "NOT SET"
}

export enum AppState {
    InitialLoad = "INITIAL LOAD",
    Transition = "TRANSITION",
    SubsquentLoad = "SUBSEQUENT LOAD",
    NoAnimation = "NO ANIMATION"
}

export enum SideMenuState {
    Open,
    Closed
}

export enum TrainingModule {
    Kiosk = "Kiosk",
    SmartShelves = "Smart Shelf",
    Hub = "Mobile Hub",
    ScanningSutures = "Scanning Sutures",
    OrderManagement = "Order Management",
    ExpiryManagement = "Expiry Management"
}

export interface UserTraining {
    role: UserRole
    required: TrainingModule[]
    optional?: TrainingModule[]
}

export interface NavStackItem {
    path?: string
    trainingModule?: TrainingModule
    subModuleIndex?: number
    stepNumber?: number
}

export interface NavState {
    stack: NavStackItem[]
}

export enum InventoryChangeType {
    Adjust,
    Restock
}

export interface InventoryState {
    inventory: number
    changeType: InventoryChangeType
}

export interface InventoryStateChange {
    type: InventoryChangeType
}

export interface InventoryValueChange {
    value: number
}

export interface LoadingStateChange {
    loading: boolean
}

export enum LMHelpModalLocation {
    Top,
    Right,
    Bottom,
    Left,
    Center
}

export enum DragDirection {
    LeftToRight = 2,
    RightToLeft = 1,
    TopToBottom = 3,
    BottomToTop = 4
}

export enum StepType {
    Sequence = 1,
    Movie = 2,
    Image = 3,
    Inventory = 4
}

export enum SequenceType {
    Progress = 1,
    Draggable = 2,
    Pinchable = 3,
    TapToProgress = 4
}

export interface Coordinate {
    x: number
    y: number
}

export interface Size {
    height: number
    width: number
}

export interface Rect {
    origin: Coordinate
    size: Size
}

export interface DragInfo {
    origin: Coordinate,
    rotation: number,
    scale: number
}

export interface ModalInfo {
    location: LMHelpModalLocation
    imagePath?: string
    imageSize?: Size
    descriptionUsesHTML?: boolean
    isInteractive?: boolean
    hideArrow?: boolean
}

export interface SequenceInfo {
    prefix: string;
    start: number;
    end: number;
    suffix: string;
    type: SequenceType;
    dragDirection?: number;
    framesPerSecond?: number;
}

export interface StepInfo {
    type: StepType
    title: string
    description?: string
    sequenceInfo?: SequenceInfo
    imagePath?: string
    tapLocation?: Coordinate
    tappableArea?: Rect,
    showTappableArea?: boolean,
    dragInfo?: DragInfo,
    imageIsFull?: boolean
    imageIsCenter?: boolean
    modalInfo?: ModalInfo
    altRoute?: string
}

export interface LearningModule {
    steps: StepInfo[]
    name: string
    description: string
    cardWidth: number
    recommendedModules: TrainingModule[]
    suggestedNextSteps?: LearningModule[]
    sequenceInfo?: SequenceInfo
}

export interface Training {
    trainingModule: TrainingModule
    subModules: LearningModule[]
    alternateModules?: LearningModule[]
    currentSubModuleIndex: number
    currentStepIndex: number
    recommendedModules: TrainingModule[]
    howItWorks?: string[]
    note?: string
}

export interface TrainingState {
    currentTraining?: Training
}

export const KioskSubRetrieve: LearningModule = {
    steps: [
        {
            type: StepType.Image,
            title: "Retrieve Pick Sheet(s)",
            description: "After logging in to the AITA™ Engine, select the Operations & Insights tab.",
            imagePath: "/assets/images/AITA_Gateway_Main.png",
            imageIsCenter: true,
            modalInfo: {
                location: LMHelpModalLocation.Bottom,
                isInteractive: false
            },
            tapLocation: {
                x: 1183,
                y: 200,
            }
        },
        {
            type: StepType.Image,
            title: "Retrieve Pick Sheet(s)",
            description: "Now select Case Status from the menu.",
            imagePath: "/assets/images/AITA_Gateway_Main_With_Menu.png",
            imageIsCenter: true,
            modalInfo: {
                location: LMHelpModalLocation.Bottom,
                isInteractive: false
            },
            tapLocation: {
                x: 1030,
                y: 302,
            }
        },
        {
            type: StepType.Image,
            title: "Retrieve Pick Sheet(s)",
            description: "After selecting the correct date, select Print All.",
            imagePath: "/assets/images/AITA_Gateway_Case.png",
            imageIsCenter: true,
            modalInfo: {
                location: LMHelpModalLocation.Bottom,
                isInteractive: false
            },
            tapLocation: {
                x: 1257,
                y: 570,
            }
        },
        {
            type: StepType.Image,
            title: "Pick Sheet(s) Retrieved",
            imagePath: "/assets/images/Pick-Sheet_kiosk.png",
            modalInfo: {
                location: LMHelpModalLocation.Right,
                isInteractive: true
            },
            tappableArea: {
                origin: {
                    x: 0,
                    y: 0,
                },
                size: {
                    width: 1920,
                    height: 1080
                }
            },
            showTappableArea: true
        },
        {
            type: StepType.Image,
            title: "Identify procedure",
            description: "Surgeon, procedure, room, time, and date will be indicated on the screen adjacent to the locker.",
            imagePath: "/assets/sequences/Kiosk_1_SutureRetrieval/Kiosk_1_SutureRetrieval_00000.jpg",
            imageIsFull: true,
            modalInfo: {
                location: LMHelpModalLocation.Right,
                isInteractive: true,
                imagePath: "/assets/images/mini_screen@2x.png",
                imageSize: {
                    width: 166,
                    height: 113
                }
            }
        },
        {
            type: StepType.Sequence,
            title: "Open the indicated locker by selecting it.",
            sequenceInfo: {
                prefix: "/assets/sequences/Kiosk_1_SutureRetrieval/Kiosk_1_SutureRetrieval_00",
                start: 0,
                end: 124,
                suffix: "jpg",
                type: SequenceType.TapToProgress
            },
            modalInfo: {
                location: LMHelpModalLocation.Bottom,
                isInteractive: false
            },
            tapLocation: {
                x: 805,
                y: 430,
            }
        },
        {
            type: StepType.Sequence,
            title: "Pull out the bin by selecting and dragging where indicated.",
            sequenceInfo: {
                prefix: "/assets/sequences/Kiosk_1_SutureRetrieval/Kiosk_1_SutureRetrieval_00",
                start: 125,
                end: 175,
                suffix: "jpg",
                type: SequenceType.Draggable,
                dragDirection: DragDirection.RightToLeft,
                framesPerSecond: 90
            },
            dragInfo: {
                origin: {
                    x: 500,
                    y: 442
                },
                rotation: -100,
                scale: 1.5
            },
            modalInfo: {
                location: LMHelpModalLocation.Bottom,
                isInteractive: false
            }
        },
        {
            type: StepType.Sequence,
            title: "Select the sutures to remove",
            description: "Be sure to check locker bin thoroughly to ensure all sutures have been removed as some sutures may be at the back of the bin. Sutures should still be scanned in the OR during the procedure, once used.",
            sequenceInfo: {
                prefix: "/assets/sequences/Kiosk_1_SutureRetrieval/Kiosk_1_SutureRetrieval_00",
                start: 176,
                end: 255,
                suffix: "jpg",
                type: SequenceType.TapToProgress
            },
            modalInfo: {
                location: LMHelpModalLocation.Bottom,
                isInteractive: false
            },
            tapLocation: {
                x: 632,
                y: 578,
            }
        },
        {
            type: StepType.Sequence,
            title: "Push the bin back into place by selecting and dragging where indicated.",
            sequenceInfo: {
                prefix: "/assets/sequences/Kiosk_1_SutureRetrieval/Kiosk_1_SutureRetrieval_00",
                start: 256,
                end: 292,
                suffix: "jpg",
                type: SequenceType.Draggable,
                dragDirection: DragDirection.LeftToRight
            },
            dragInfo: {
                origin: {
                    x: 500,
                    y: 442
                },
                rotation: 70,
                scale: 1.5
            },
            modalInfo: {
                location: LMHelpModalLocation.Bottom,
                isInteractive: false
            },
        },
        {
            type: StepType.Sequence,
            title: "Close the door by selecting where indicated.",
            sequenceInfo: {
                prefix: "/assets/sequences/Kiosk_1_SutureRetrieval/Kiosk_1_SutureRetrieval_00",
                start: 293,
                end: 340,
                suffix: "jpg",
                type: SequenceType.TapToProgress
            },
            modalInfo: {
                location: LMHelpModalLocation.Bottom,
                isInteractive: false
            },
            tapLocation: {
                x: 280,
                y: 450,
            }
        },
        {
            type: StepType.Sequence,
            title: "Press the locker clearance button",
            description: "If this step is neglected, the locker will not be able to be used for future cases.",
            sequenceInfo: {
                prefix: "/assets/sequences/Kiosk_1_SutureRetrieval/Kiosk_1_SutureRetrieval_00",
                start: 341,
                end: 343,
                suffix: "jpg",
                type: SequenceType.TapToProgress
            },
            modalInfo: {
                location: LMHelpModalLocation.Bottom,
                isInteractive: false
            },
            tapLocation: {
                x: 1478,
                y: 722,
            }
        }
    ],
    name: "Retrieving",
    description: "Retrieving Sutures from the Kiosk.",
    cardWidth: 333,
    recommendedModules: [TrainingModule.SmartShelves, TrainingModule.Hub],
    sequenceInfo: {
        type: SequenceType.Progress,
        prefix: "/assets/sequences/Kiosk_1_SutureRetrieval/Kiosk_1_SutureRetrieval_00",
        start: 0,
        end: 343,
        suffix: 'jpg'
    }
}
export const KioskSubRestocking: LearningModule = {
    steps: [
        {
            type: StepType.Image,
            title: "Loading Stack",
            description: `First you will need to fill a Loading Stack with returned sutures.<br/>
            <span style="font-style:italic;font-size:16px">Best practice: Suture returns by the Kiosk should be initiated once same- or next-day picking is finished</span>
            <ul class='modal-list'>
                <li>Choose the correct-size Loading Stack based on the individual suture packaging size (small, medium, large)</li>
                <li>Ensure that the bar codes are facing up on the right side of the pack and that the sutures are laying flat in the stack.</li>
                <li>There is a sticker on the stack that lets you know the correct orientation for the stack.</li>
            </ul>`,
            imagePath: "/assets/images/BinStack.png",
            imageIsFull: true,
            modalInfo: {
                location: LMHelpModalLocation.Right,
                descriptionUsesHTML: true,
                isInteractive: true
            }
        },
        {
            type: StepType.Sequence,
            title: "",
            description: "",
            sequenceInfo: {
                prefix: "/assets/sequences/Kiosk_2_SutureRestocking/Kiosk_2_SutureRestocking_000",
                start: 0,
                end: 50,
                suffix: "jpg",
                type: SequenceType.Progress
            }
        },
        {
            type: StepType.Sequence,
            title: "Kiosk screen",
            description: "On the Kiosk screen, select \"Return unused sutures.\" This will unlock the Kiosk doors.",
            sequenceInfo: {
                prefix: "/assets/sequences/Kiosk_2_SutureRestocking/Kiosk_2_SutureRestocking_00",
                start: 50,
                end: 116,
                suffix: "jpg",
                type: SequenceType.TapToProgress
            },
            modalInfo: {
                location: LMHelpModalLocation.Bottom,
                isInteractive: false
            },
            tapLocation: {
                x: 905,
                y: 471,
            },
            tappableArea: {
                origin: {
                    x: 803,
                    y: 487,
                },
                size: {
                    width: 323,
                    height: 72
                }
            }
        },
        {
            type: StepType.Sequence,
            title: "Open the left door by selecting it.",
            sequenceInfo: {
                prefix: "/assets/sequences/Kiosk_2_SutureRestocking/Kiosk_2_SutureRestocking_00",
                start: 117,
                end: 216,
                suffix: "jpg",
                type: SequenceType.TapToProgress
            },
            modalInfo: {
                location: LMHelpModalLocation.Bottom,
                isInteractive: false
            },
            tapLocation: {
                x: 1162,
                y: 601,
            },
            tappableArea: {
                origin: {
                    x: 920,
                    y: 123,
                },
                size: {
                    width: 512,
                    height: 957
                }
            }
        },
        {
            type: StepType.Sequence,
            title: "Remove the empty Loading Stack by selecting it.",
            sequenceInfo: {
                prefix: "/assets/sequences/Kiosk_2_SutureRestocking/Kiosk_2_SutureRestocking_00",
                start: 217,
                end: 283,
                suffix: "jpg",
                type: SequenceType.TapToProgress
            },
            modalInfo: {
                location: LMHelpModalLocation.Bottom,
                isInteractive: false
            },
            tapLocation: {
                x: 900,
                y: 440,
            },
            tappableArea: {
                origin: {
                    x: 691,
                    y: 123,
                },
                size: {
                    width: 512,
                    height: 717
                }
            }
        },
        {
            type: StepType.Sequence,
            title: "Return the new Loading Stack by selecting and dragging in the indicated direction.",
            sequenceInfo: {
                prefix: "/assets/sequences/Kiosk_2_SutureRestocking/Kiosk_2_SutureRestocking_00",
                start: 284,
                end: 321,
                suffix: "jpg",
                type: SequenceType.Draggable,
                dragDirection: DragDirection.LeftToRight
            },
            dragInfo: {
                origin: {
                    x: 400,
                    y: 720
                },
                rotation: 60,
                scale: 1.5
            },
            modalInfo: {
                location: LMHelpModalLocation.Bottom,
                isInteractive: false
            }
        },
        {
            type: StepType.Sequence,
            title: "Rotate Loading Stack counterclockwise into the correct position by selecting and dragging in the indicated direction.",
            sequenceInfo: {
                prefix: "/assets/sequences/Kiosk_2_SutureRestocking/Kiosk_2_SutureRestocking_00",
                start: 321,
                end: 351,
                suffix: "jpg",
                type: SequenceType.Draggable,
                dragDirection: DragDirection.LeftToRight
            },
            dragInfo: {
                origin: {
                    x: 930,
                    y: 340
                },
                rotation: 96,
                scale: 1.5
            },
            modalInfo: {
                location: LMHelpModalLocation.Bottom,
                isInteractive: false
            }
        },
        {
            type: StepType.Sequence,
            title: "Place the Loading Stack by selecting and dragging in the indicated direction.",
            sequenceInfo: {
                prefix: "/assets/sequences/Kiosk_2_SutureRestocking/Kiosk_2_SutureRestocking_00",
                start: 351,
                end: 385,
                suffix: "jpg",
                type: SequenceType.Draggable,
                dragDirection: DragDirection.TopToBottom
            },
            dragInfo: {
                origin: {
                    x: 930,
                    y: 340
                },
                rotation: 180,
                scale: 1.5
            },
            modalInfo: {
                location: LMHelpModalLocation.Bottom,
                isInteractive: false
            }
        },
        {
            type: StepType.Sequence,
            title: "Close the door by selecting the indicated location.",
            sequenceInfo: {
                prefix: "/assets/sequences/Kiosk_2_SutureRestocking/Kiosk_2_SutureRestocking_00",
                start: 385,
                end: 496,
                suffix: "jpg",
                type: SequenceType.TapToProgress
            },
            modalInfo: {
                location: LMHelpModalLocation.Bottom,
                isInteractive: false
            },
            tapLocation: {
                x: 300,
                y: 600
            }
        },
        {
            type: StepType.Image,
            title: "Locking Kiosk doors",
            description: "After you have closed the door, pressing the \"Lock Doors\" button on the Kiosk screen will lock the doors. If the doors do not lock properly, the Kiosk screen will indicate a \"Door lock failed\" error message. You will need to ensure the doors are pushed all the way closed and try again.",
            imagePath: "/assets/sequences/Kiosk_2_SutureRestocking/Kiosk_2_SutureRestocking_00496.jpg",
            imageIsFull: true,
            modalInfo: {
                location: LMHelpModalLocation.Bottom,
                isInteractive: false
            },
            tapLocation: {
                x: 900,
                y: 635,
            },
            tappableArea: {
                origin: {
                    x: 807,
                    y: 653,
                },
                size: {
                    width: 305,
                    height: 65
                }
            }
        }
    ],
    name: "Returning",
    description: "Returning Sutures to the Kiosk.",
    cardWidth: 333,
    recommendedModules: [TrainingModule.SmartShelves, TrainingModule.Hub],
    sequenceInfo: {
        type: SequenceType.Progress,
        prefix: "/assets/sequences/Kiosk_2_SutureRestocking/Kiosk_2_SutureRestocking_00",
        start: 0,
        end: 496,
        suffix: 'jpg'
    }
}
export const KioskSubReplenishing: LearningModule = {
    steps: [
        {
            type: StepType.Image,
            title: "On the Smart Shelf screen",
            description: "Open the menu by clicking on the 3 horizontal lines in the upper left corner and click \"Request Transfers.\" This ensures that transfers are generated based on real-time inventory levels",
            imagePath: "/assets/images/Replenishment_via_SmartShelf_tranx_1.png",
            imageIsFull: true,
            modalInfo: {
                location: LMHelpModalLocation.Bottom,
                isInteractive: false
            },
            tapLocation: {
                x: 667,
                y: 72
            },
        },
        {
            type: StepType.Image,
            title: "On the Smart Shelf screen",
            description: "Open the menu by clicking on the 3 horizontal lines in the upper left corner and click \"Request Transfers.\" This ensures that transfers are generated based on real-time inventory levels",
            imagePath: "/assets/images/Replenishment_via_SmartShelf_tranx_2.png",
            imageIsFull: true,
            modalInfo: {
                location: LMHelpModalLocation.Bottom,
                isInteractive: false
            },
            tapLocation: {
                x: 760,
                y: 318
            },
            tappableArea: {
                origin: {
                    x: 710,
                    y: 349
                },
                size: {
                    width: 218,
                    height: 47
                }
            }
        },
        {
            type: StepType.Image,
            title: "Select the device that you are replenishing.",
            imagePath: "/assets/images/Replenishment_via_SmartShelf_tranx_3.png",
            imageIsFull: true,
            modalInfo: {
                location: LMHelpModalLocation.Bottom,
                isInteractive: false
            },
            tapLocation: {
                x: 910,
                y: 235
            },
            tappableArea: {
                origin: {
                    x: 740,
                    y: 241
                },
                size: {
                    width: 447,
                    height: 100
                }
            }
        },
        {
            type: StepType.Sequence,
            title: "",
            description: "",
            sequenceInfo: {
                type: SequenceType.Progress,
                prefix: "/assets/sequences/Kiosk_Replenishment_via_SmartShelf/Kiosk_Replenishment_via_SmartShelf_tranx_000",
                start: 0,
                end: 52,
                suffix: 'jpg'
            }
        },
        {
            type: StepType.Sequence,
            title: "The Smart Shelf LED displays will indicate the quantity of each product code that needs to be picked for transfer.",
            description: "Remove that quantity of inventory from the corresponding Suture Bin. Do not remove the Suture Bin from the Smart Shelf when picking or returning sutures.",
            sequenceInfo: {
                type: SequenceType.TapToProgress,
                start: 52,
                end: 142,
                prefix: "/assets/sequences/Kiosk_Replenishment_via_SmartShelf/Kiosk_Replenishment_via_SmartShelf_tranx_00",
                suffix: 'jpg'
            },
            tapLocation: {
                x: 860,
                y: 160
            },
            modalInfo: {
                location: LMHelpModalLocation.Bottom,
                isInteractive: false,
            }
        },
        {
            type: StepType.Sequence,
            title: "The Smart Shelf LED displays will indicate the quantity of each product code that needs to be picked for transfer.",
            description: "Remove that quantity of inventory from the corresponding Suture Bin. Do not remove the Suture Bin from the Smart Shelf when picking or returning sutures.",
            sequenceInfo: {
                type: SequenceType.TapToProgress,
                start: 142,
                end: 271,
                prefix: "/assets/sequences/Kiosk_Replenishment_via_SmartShelf/Kiosk_Replenishment_via_SmartShelf_tranx_00",
                suffix: 'jpg'
            },
            tapLocation: {
                x: 945,
                y: 160
            },
            modalInfo: {
                location: LMHelpModalLocation.Bottom,
                isInteractive: false,
            }
        },
        {
            type: StepType.Sequence,
            title: "The Smart Shelf LED displays will indicate the quantity of each product code that needs to be picked for transfer.",
            description: "Remove that quantity of inventory from the corresponding Suture Bin. Do not remove the Suture Bin from the Smart Shelf when picking or returning sutures.",
            sequenceInfo: {
                type: SequenceType.TapToProgress,
                start: 271,
                end: 390,
                prefix: "/assets/sequences/Kiosk_Replenishment_via_SmartShelf/Kiosk_Replenishment_via_SmartShelf_tranx_00",
                suffix: 'jpg'
            },
            tapLocation: {
                x: 942,
                y: 480
            },
            modalInfo: {
                location: LMHelpModalLocation.Bottom,
                isInteractive: false,
            }
        },
        {
            type: StepType.Sequence,
            title: "The Smart Shelf LED displays will indicate the quantity of each product code that needs to be picked for transfer.",
            description: "Remove that quantity of inventory from the corresponding Suture Bin. Do not remove the Suture Bin from the Smart Shelf when picking or returning sutures.",
            sequenceInfo: {
                type: SequenceType.TapToProgress,
                start: 390,
                end: 590,
                prefix: "/assets/sequences/Kiosk_Replenishment_via_SmartShelf/Kiosk_Replenishment_via_SmartShelf_tranx_00",
                suffix: 'jpg'
            },
            tapLocation: {
                x: 1143,
                y: 308
            },
            modalInfo: {
                location: LMHelpModalLocation.Bottom,
                isInteractive: false,
            }
        },
        {
            type: StepType.Sequence,
            title: "",
            description: "",
            sequenceInfo: {
                type: SequenceType.Progress,
                prefix: "/assets/sequences/Kiosk_Replenishment_via_SmartShelf/Kiosk_Replenishment_via_SmartShelf_tranx_00",
                start: 590,
                end: 645,
                suffix: 'jpg'
            }
        },
        {
            type: StepType.Image,
            title: "When finished picking sutures",
            description: "Select \"Done\" on the Smart Shelf screen to alert the system that picking is complete.",
            imagePath: "/assets/sequences/Kiosk_Replenishment_via_SmartShelf/Kiosk_Replenishment_via_SmartShelf_tranx_00646.jpg",
            imageIsFull: true,
            modalInfo: {
                location: LMHelpModalLocation.Bottom,
                isInteractive: false
            },
            tapLocation: {
                x: 1020,
                y: 568,
            },
            tappableArea: {
                origin: {
                    x: 990,
                    y: 580,
                },
                size: {
                    width: 180,
                    height: 80
                }
            }
        },
        {
            type: StepType.Image,
            title: "Kiosk Loading Stack",
            description: `First you will need to fill a Loading Stack with the transferred sutures.<br/>
            <ul class='modal-list'>
                <li>Choose the correct-size Loading Stack based on the individual suture packaging size (small, medium, large)</li>
                <li>Ensure that the bar codes are facing up and that the sutures are laying flat in the stack.
                    <ul>
                        <li>Sutures with foil envelopes should be loaded with the <b>colored label facing up</b></li>
                        <li>Sutures with paper envelopes should be loaded with the <b>paper side facing up</b></li>
                    </ul>
                </li>
                <li>There is a sticker on the stack that lets you know the correct orientation for the stack.</li>
            </ul>`,
            imagePath: "/assets/images/BinStack.png",
            imageIsFull: true,
            modalInfo: {
                location: LMHelpModalLocation.Right,
                isInteractive: true,
                descriptionUsesHTML: true
            }
        },
        {
            type: StepType.Sequence,
            title: "Kiosk screen",
            description: "On the Kiosk screen, select \"Load Sutures.\" This will unlock the Kiosk doors.",
            sequenceInfo: {
                prefix: "/assets/sequences/Kiosk_Replenishment_via_SmartShelf/Kiosk_Replenishment_via_SmartShelf_tranx_00",
                start: 647,
                end: 713,
                suffix: "jpg",
                type: SequenceType.TapToProgress
            },
            modalInfo: {
                location: LMHelpModalLocation.Bottom,
                isInteractive: false
            },
            tapLocation: {
                x: 905,
                y: 400,
            },
            tappableArea: {
                origin: {
                    x: 803,
                    y: 419,
                },
                size: {
                    width: 323,
                    height: 72
                }
            }
        },
        {
            type: StepType.Sequence,
            title: "Open the left door by selecting it.",
            sequenceInfo: {
                prefix: "/assets/sequences/Kiosk_Replenishment_via_SmartShelf/Kiosk_Replenishment_via_SmartShelf_tranx_00",
                start: 713,
                end: 820,
                suffix: "jpg",
                type: SequenceType.TapToProgress
            },
            modalInfo: {
                location: LMHelpModalLocation.Bottom,
                isInteractive: false
            },
            tapLocation: {
                x: 1162,
                y: 601,
            },
            tappableArea: {
                origin: {
                    x: 920,
                    y: 123,
                },
                size: {
                    width: 512,
                    height: 957
                }
            }
        },
        {
            type: StepType.Sequence,
            title: "Remove the empty Loading Stack by selecting it.",
            sequenceInfo: {
                prefix: "/assets/sequences/Kiosk_Replenishment_via_SmartShelf/Kiosk_Replenishment_via_SmartShelf_tranx_00",
                start: 821,
                end: 885,
                suffix: "jpg",
                type: SequenceType.TapToProgress
            },
            modalInfo: {
                location: LMHelpModalLocation.Bottom,
                isInteractive: false
            },
            tapLocation: {
                x: 900,
                y: 440,
            },
            tappableArea: {
                origin: {
                    x: 691,
                    y: 123,
                },
                size: {
                    width: 512,
                    height: 717
                }
            }
        },
        {
            type: StepType.Sequence,
            title: "Return the new Loading Stack by selecting and dragging in the indicated direction.",
            sequenceInfo: {
                prefix: "/assets/sequences/Kiosk_Replenishment_via_SmartShelf/Kiosk_Replenishment_via_SmartShelf_tranx_00",
                start: 886,
                end: 924,
                suffix: "jpg",
                type: SequenceType.Draggable,
                dragDirection: DragDirection.LeftToRight
            },
            dragInfo: {
                origin: {
                    x: 400,
                    y: 720
                },
                rotation: 60,
                scale: 1.5
            },
            modalInfo: {
                location: LMHelpModalLocation.Bottom,
                isInteractive: false
            }
        },
        {
            type: StepType.Sequence,
            title: "Rotate Loading Stack counterclockwise into the correct position by selecting and dragging in the indicated direction.",
            sequenceInfo: {
                prefix: "/assets/sequences/Kiosk_Replenishment_via_SmartShelf/Kiosk_Replenishment_via_SmartShelf_tranx_00",
                start: 924,
                end: 954,
                suffix: "jpg",
                type: SequenceType.Draggable,
                dragDirection: DragDirection.LeftToRight
            },
            dragInfo: {
                origin: {
                    x: 930,
                    y: 340
                },
                rotation: 96,
                scale: 1.5
            },
            modalInfo: {
                location: LMHelpModalLocation.Bottom,
                isInteractive: false
            }
        },
        {
            type: StepType.Sequence,
            title: "Place the Loading Stack by selecting and dragging in the indicated direction.",
            sequenceInfo: {
                prefix: "/assets/sequences/Kiosk_Replenishment_via_SmartShelf/Kiosk_Replenishment_via_SmartShelf_tranx_00",
                start: 954,
                end: 984,
                suffix: "jpg",
                type: SequenceType.Draggable,
                dragDirection: DragDirection.TopToBottom
            },
            dragInfo: {
                origin: {
                    x: 930,
                    y: 340
                },
                rotation: 180,
                scale: 1.5
            },
            modalInfo: {
                location: LMHelpModalLocation.Bottom,
                isInteractive: false
            }
        },
        {
            type: StepType.Sequence,
            title: "Close the door by selecting the indicated location.",
            sequenceInfo: {
                prefix: "/assets/sequences/Kiosk_Replenishment_via_SmartShelf/Kiosk_Replenishment_via_SmartShelf_tranx_0",
                start: 984,
                end: 1104,
                suffix: "jpg",
                type: SequenceType.TapToProgress
            },
            modalInfo: {
                location: LMHelpModalLocation.Bottom,
                isInteractive: false
            },
            tapLocation: {
                x: 300,
                y: 600
            }
        },
        {
            type: StepType.Image,
            title: "Locking Kiosk doors",
            description: "After you have closed the door, pressing the \"Lock Doors\" button on the Kiosk screen will lock the doors. If the doors do not lock properly, the Kiosk screen will indicate a \"Door lock failed\" error message. You will need to ensure the doors are pushed all the way closed and try again.",
            imagePath: "/assets/sequences/Kiosk_Replenishment_via_SmartShelf/Kiosk_Replenishment_via_SmartShelf_tranx_01104.jpg",
            imageIsFull: true,
            modalInfo: {
                location: LMHelpModalLocation.Bottom,
                isInteractive: false
            },
            tapLocation: {
                x: 900,
                y: 635,
            },
            tappableArea: {
                origin: {
                    x: 807,
                    y: 653,
                },
                size: {
                    width: 305,
                    height: 65
                }
            }
        }
    ],
    name: "Transfer",
    description: "Transfer Sutures to the Kiosk",
    cardWidth: 333,
    recommendedModules: [TrainingModule.SmartShelves, TrainingModule.Hub],
    sequenceInfo: {
        type: SequenceType.Progress,
        prefix: "/assets/sequences/Kiosk_Replenishment_via_SmartShelf/Kiosk_Replenishment_via_SmartShelf_tranx_0",
        start: 0,
        end: 1104,
        suffix: 'jpg'
    }
}
export const KioskTraining: Training = {
    trainingModule: TrainingModule.Kiosk,
    subModules: [KioskSubRetrieve, KioskSubRestocking, KioskSubReplenishing],    
    currentSubModuleIndex: 0,
    currentStepIndex: 0,
    howItWorks: [
        "To retrieve sutures, a series of pick heads in the center bay navigate the Suture Stacks to pick sutures for a procedure based on the procedure schedule and surgeon preference card",
        "The pick head scans the suture to verify product code and expiration date",
        "Once verified, the suture is picked up by a suction cup and moved to a locker for retrieval by the hospital staff",
        "When returning sutures, the pick heads will navigate to the Loading Stack and scan the suture to verify product code and expiry",
        "Once verified, the suture is picked up by a suction cup and moved to the correct Suture Stack where it is returned for future picking."
    ],
    recommendedModules: [TrainingModule.Hub, TrainingModule.SmartShelves, TrainingModule.ScanningSutures, TrainingModule.OrderManagement, TrainingModule.ExpiryManagement],
}

export const SmartShelvesSubRetrieve: LearningModule = {
    steps: [
        {
            type: StepType.Image,
            title: "Retrieve Pick Sheet(s)",
            description: "After Logging in to the AITA™ Engine, select the Operations & Insights tab.",
            imagePath: "/assets/images/AITA_Gateway_Main.png",
            imageIsCenter: true,
            modalInfo: {
                location: LMHelpModalLocation.Bottom,
                isInteractive: false
            },
            tapLocation: {
                x: 1183,
                y: 200,
            }
        },
        {
            type: StepType.Image,
            title: "Retrieve Pick Sheet(s)",
            description: "Now select Case Status from the menu.",
            imagePath: "/assets/images/AITA_Gateway_Main_With_Menu.png",
            imageIsCenter: true,
            modalInfo: {
                location: LMHelpModalLocation.Bottom,
                isInteractive: false
            },
            tapLocation: {
                x: 1030,
                y: 302,
            }
        },
        {
            type: StepType.Image,
            title: "Retrieve Pick Sheet(s)",
            description: "After selecting the correct date, select Print All.",
            imagePath: "/assets/images/AITA_Gateway_Case.png",
            imageIsCenter: true,
            modalInfo: {
                location: LMHelpModalLocation.Bottom,
                isInteractive: false
            },
            tapLocation: {
                x: 1257,
                y: 570,
            }
        },
        {
            type: StepType.Image,
            title: "Pick Sheet(s) Retrieved",
            imagePath: "/assets/images/Pick-Sheet_SmartShelf.png",
            modalInfo: {
                location: LMHelpModalLocation.Right,
                isInteractive: true
            },
            tappableArea: {
                origin: {
                    x: 0,
                    y: 0,
                },
                size: {
                    width: 1920,
                    height: 1080
                }
            },
            showTappableArea: true
        },
        {
            type: StepType.Sequence,
            title: "",
            description: "",
            sequenceInfo: {
                type: SequenceType.Progress,
                prefix: "/assets/sequences/SmartShelves_1_SutureRetrieval/SmartShelf_1_Retrieving_000",
                start: 0,
                end: 60,
                suffix: "jpg"
            }
        },
        {
            type: StepType.Sequence,
            title: "Smart Shelf screen",
            description: "Select the procedure you are picking for. This should match the details on the respective AITA™ Pick Sheet. For add-on cases, select the \"ADD-ON CASE\" button.",
            sequenceInfo: {
                type: SequenceType.TapToProgress,
                prefix: "/assets/sequences/SmartShelves_1_SutureRetrieval/SmartShelf_1_Retrieving_00",
                start: 60,
                end: 130,
                suffix: 'jpg'
            },
            modalInfo: {
                location: LMHelpModalLocation.Right,
                isInteractive: false,
                hideArrow: true,
            },
            tapLocation: {
                x: 909,
                y: 250
            },
            tappableArea: {
                origin: {
                    x: 700,
                    y: 260,
                },
                size: {
                    width: 520,
                    height: 670
                }
            }
        },
        {
            type: StepType.Sequence,
            title: "The Smart Shelf LED displays will indicate the quantity of each product code that needs to be picked.",
            description: "Remove that quantity of inventory from the corresponding Suture Bin. Do not remove the Suture Bin from the Smart Shelf when picking or returning sutures.",
            sequenceInfo: {
                type: SequenceType.TapToProgress,
                start: 130,
                end: 220,
                prefix: "/assets/sequences/SmartShelves_1_SutureRetrieval/SmartShelf_1_Retrieving_00",
                suffix: 'jpg'
            },
            tapLocation: {
                x: 860,
                y: 160
            },
            modalInfo: {
                location: LMHelpModalLocation.Bottom,
                isInteractive: false,
            }
        },
        {
            type: StepType.Sequence,
            title: "The Smart Shelf LED displays will indicate the quantity of each product code that needs to be picked.",
            description: "Remove that quantity of inventory from the corresponding Suture Bin. Do not remove the Suture Bin from the Smart Shelf when picking or returning sutures.",
            sequenceInfo: {
                type: SequenceType.TapToProgress,
                start: 221,
                end: 350,
                prefix: "/assets/sequences/SmartShelves_1_SutureRetrieval/SmartShelf_1_Retrieving_00",
                suffix: 'jpg'
            },
            tapLocation: {
                x: 945,
                y: 160
            },
            modalInfo: {
                location: LMHelpModalLocation.Bottom,
                isInteractive: false,
            }
        },
        {
            type: StepType.Sequence,
            title: "The Smart Shelf LED displays will indicate the quantity of each product code that needs to be picked.",
            description: "Remove that quantity of inventory from the corresponding Suture Bin. Do not remove the Suture Bin from the Smart Shelf when picking or returning sutures.",
            sequenceInfo: {
                type: SequenceType.TapToProgress,
                start: 351,
                end: 470,
                prefix: "/assets/sequences/SmartShelves_1_SutureRetrieval/SmartShelf_1_Retrieving_00",
                suffix: 'jpg'
            },
            tapLocation: {
                x: 942,
                y: 480
            },
            modalInfo: {
                location: LMHelpModalLocation.Bottom,
                isInteractive: false,
            }
        },
        {
            type: StepType.Sequence,
            title: "The Smart Shelf LED displays will indicate the quantity of each product code that needs to be picked.",
            description: "Remove that quantity of inventory from the corresponding Suture Bin. Do not remove the Suture Bin from the Smart Shelf when picking or returning sutures.",
            sequenceInfo: {
                type: SequenceType.TapToProgress,
                start: 470,
                end: 670,
                prefix: "/assets/sequences/SmartShelves_1_SutureRetrieval/SmartShelf_1_Retrieving_00",
                suffix: 'jpg'
            },
            tapLocation: {
                x: 1143,
                y: 308
            },
            modalInfo: {
                location: LMHelpModalLocation.Bottom,
                isInteractive: false,
            }
        },
        {
            type: StepType.Sequence,
            title: "",
            description: "",
            sequenceInfo: {
                type: SequenceType.Progress,
                prefix: "/assets/sequences/SmartShelves_1_SutureRetrieval/SmartShelf_1_Retrieving_00",
                start: 670,
                end: 725,
                suffix: 'jpg'
            }
        },
        {
            type: StepType.Image,
            title: "When finished picking sutures",
            description: "Select \"Done\" on the Smart Shelf screen to alert the system that picking is complete. Sutures should still be scanned in the OR during the procedure, once used.",
            imagePath: "/assets/sequences/SmartShelves_1_SutureRetrieval/SmartShelf_1_Retrieving_00725.jpg",
            imageIsFull: true,
            modalInfo: {
                location: LMHelpModalLocation.Bottom,
                isInteractive: false
            },
            tapLocation: {
                x: 1020,
                y: 568,
            },
            tappableArea: {
                origin: {
                    x: 990,
                    y: 580,
                },
                size: {
                    width: 180,
                    height: 80
                }
            }
        }
    ],
    name: "Retrieving",
    description: "Retrieving Sutures from the Smart Shelf.",
    cardWidth: 400,
    recommendedModules: [TrainingModule.SmartShelves, TrainingModule.Hub, TrainingModule.Kiosk],
    sequenceInfo: {
        type: SequenceType.Progress,
        prefix: "/assets/sequences/SmartShelves_1_SutureRetrieval/SmartShelf_1_Retrieving_00",
        start: 0,
        end: 725,
        suffix: 'jpg'
    }
}
export const SmartShelvesSubRestocking: LearningModule = {
    steps: [
        {
            type: StepType.Image,
            title: "Kiosk Exception Bin",
            description: "If your hospital has a Kiosk, go to the Kiosk and grab the non-expired sutures from the Exception Bin. Then go to the Smart Shelf.</br>If your hospital does not have a Kiosk, grab the non-expired sutures being returned and go directly to the Smart Shelf.",
            imageIsFull: true,
            imagePath: "/assets/images/exception_bin.png",
            tapLocation: {
                x: 870,
                y: 437,
            },
            tappableArea: {
                origin: {
                    x: 720,
                    y: 374,
                },
                size: {
                    width: 428,
                    height: 270
                }
            },
            modalInfo: {
                location: LMHelpModalLocation.Bottom,
                isInteractive: false,
                descriptionUsesHTML: true
            }
        },
        {
            type: StepType.Sequence,
            title: "",
            sequenceInfo: {
                type: SequenceType.Progress,
                prefix: "/assets/sequences/SmartShelves_2_SutureRestocking/SmartShelf_2_SutureRestock_000",
                start: 0,
                end: 60,
                suffix: "jpg"
            }
        },
        {
            type: StepType.Sequence,
            title: "Smart Shelf screen",
            description: "On the Smart Shelf screen, select \"Return\" at the bottom of the screen.",
            sequenceInfo: {
                type: SequenceType.TapToProgress,
                prefix: "/assets/sequences/SmartShelves_2_SutureRestocking/SmartShelf_2_SutureRestock_000",
                start: 60,
                end: 79,
                suffix: "jpg"
            },
            tapLocation: {
                x: 1030,
                y: 877,
            },
            tappableArea: {
                origin: {
                    x: 960,
                    y: 914,
                },
                size: {
                    width: 251,
                    height: 40
                }
            },
            modalInfo: {
                location: LMHelpModalLocation.Right,
                isInteractive: false,
                hideArrow: true
            }
        },
        {
            type: StepType.Sequence,
            title: "Scan the QR Code on the Suture with the Smart Shelf Scanner.",
            description: "Tap scanner to initiate scan.",
            sequenceInfo: {
                type: SequenceType.TapToProgress,
                prefix: "/assets/sequences/SmartShelves_2_SutureRestocking/SmartShelf_2_SutureRestock_00",
                start: 79,
                end: 230,
                suffix: "jpg"
            },
            tapLocation: {
                x: 430,
                y: 660,
            },
            tappableArea: {
                origin: {
                    x: 370,
                    y: 586,
                },
                size: {
                    width: 238,
                    height: 270
                }
            },
            modalInfo: {
                location: LMHelpModalLocation.Right,
                isInteractive: false,
                hideArrow: true
            }
        },
        {
            type: StepType.Sequence,
            title: "",
            sequenceInfo: {
                type: SequenceType.Progress,
                prefix: "/assets/sequences/SmartShelves_2_SutureRestocking/SmartShelf_2_SutureRestock_00",
                start: 230,
                end: 266,
                suffix: "jpg"
            }
        },
        {
            type: StepType.Sequence,
            title: "The LED Display of the corresponding Suture Bin will light up.",
            sequenceInfo: {
                type: SequenceType.TapToProgress,
                prefix: "/assets/sequences/SmartShelves_2_SutureRestocking/SmartShelf_2_SutureRestock_00",
                start: 266,
                end: 285,
                suffix: "jpg",
                
            },
            modalInfo: {
                location: LMHelpModalLocation.Left,
                isInteractive: true
            }
        },
        {
            type: StepType.Sequence,
            title: "Place the suture in the Suture Bin.",
            description: "Do not remove the Suture Bin from the Smart Shelf when picking or returning sutures.",
            sequenceInfo: {
                type: SequenceType.TapToProgress,
                prefix: "/assets/sequences/SmartShelves_2_SutureRestocking/SmartShelf_2_SutureRestock_00",
                start: 285,
                end: 365,
                suffix: "jpg",
                dragDirection: DragDirection.BottomToTop
            },
            tapLocation: {
                x: 900,
                y: 290,
            },
            tappableArea: {
                origin: {
                    x: 600,
                    y: 130,
                },
                size: {
                    width: 638,
                    height: 480
                }
            },
            modalInfo: {
                location: LMHelpModalLocation.Bottom,
                isInteractive: false
            }
        },
        {
            type: StepType.Sequence,
            title: "",
            sequenceInfo: {
                type: SequenceType.Progress,
                prefix: "/assets/sequences/SmartShelves_2_SutureRestocking/SmartShelf_2_SutureRestock_00",
                start: 365,
                end: 417,
                suffix: "jpg"
            }
        },
        {
            type: StepType.Image,
            title: "Smart Shelf screen.",
            description: "When finished, select \"Done\" on the Smart Shelf screen.",
            imagePath: "/assets/sequences/SmartShelves_2_SutureRestocking/SmartShelf_2_SutureRestock_00417.jpg",
            imageIsFull: true,
            tapLocation: {
                x: 1018,
                y: 568,
            },
            tappableArea: {
                origin: {
                    x: 1020,
                    y: 602,
                },
                size: {
                    width: 110,
                    height: 40
                }
            },
            modalInfo: {
                location: LMHelpModalLocation.Bottom,
                isInteractive: false
            }
        }
    ],
    name: "Returning",
    description: "Returning Sutures to the Smart Shelf.",
    cardWidth: 400,
    recommendedModules: [TrainingModule.Hub, TrainingModule.Kiosk],
    sequenceInfo: {
        type: SequenceType.Progress,
        prefix: "/assets/sequences/SmartShelves_2_SutureRestocking/SmartShelf_2_SutureRestock_00",
        start: 0,
        end: 271,
        suffix: 'jpg'
    }
}
export const SmartShelvesSubReplenishing: LearningModule = {
    steps: [
        {
            type: StepType.Sequence,
            title: "When new product arrives",
            description: "Sign into the AITA™ Engine on the Replenishment Scanner. Select \"Replenishment\" under the OPERATIONS & INSIGHTS dropdown menu.",
            modalInfo: {
                location: LMHelpModalLocation.Bottom,
                isInteractive: false
            },
            sequenceInfo: {
                type: SequenceType.TapToProgress,
                prefix: "/assets/sequences/SmartShelves_3_SutureReplenish/SmartShelf_Replenish_0000",
                start: 0,
                end: 1,
                suffix: 'jpg'
            },
            tapLocation: {
                x: 832,
                y: 420
            }
        },
        {
            type: StepType.Sequence,
            title: "Scan the 2D square barcode on the box of the product to be replenished.",
            description: "If the product box does not have a 2D square barcode, you will need to scan the first 1D barcode on the side of the box and will then be prompted to scan the second 1D barcode.",
            sequenceInfo: {
                type: SequenceType.TapToProgress,
                prefix: "/assets/sequences/SmartShelves_3_SutureReplenish/SmartShelf_Replenish_00",
                start: 1,
                end: 173,
                suffix: "jpg"
            },
            modalInfo: {
                location: LMHelpModalLocation.Bottom,
                isInteractive: false
            },
            tapLocation: {
                x: 930,
                y: 706
            },
            tappableArea: {
                origin: {
                    x: 823,
                    y: 737
                },
                size: {
                    width: 335,
                    height: 51
                }
            }
        },
        {
            type: StepType.Image,
            title: "The Replenishment Scanner",
            description: "Select the device into which you want to replenish product, in this case the SmartShelf.",
            imagePath: "/assets/images/selector_screen.png",
            imageIsFull: true,
            modalInfo: {
                location: LMHelpModalLocation.Bottom,
                isInteractive: false
            },
            tapLocation: {
                x: 910,
                y: 386
            },
            tappableArea: {
                origin: {
                    x: 740,
                    y: 388
                },
                size: {
                    width: 447,
                    height: 100
                }
            }
        },
        {
            type: StepType.Image,
            title: "The Replenishment Scanner",
            description: "If the selected device has more than one location for the scanned product, the scanner will display all locations.",
            imagePath: "/assets/sequences/SmartShelves_3_SutureReplenish/SmartShelf_Replenish_00173.jpg",
            imageIsFull: true,
            modalInfo: {
                location: LMHelpModalLocation.Bottom,
                isInteractive: false
            },
            tapLocation: {
                x: 1277,
                y: 663
            }
        },
        {
            type: StepType.Sequence,
            title: "",
            sequenceInfo: {
                type: SequenceType.Progress,
                prefix: "/assets/sequences/SmartShelves_3_SutureReplenish/SmartShelf_Replenish_00",
                start: 173,
                end: 215,
                suffix: 'jpg'
            }
        },
        {
            type: StepType.Sequence,
            title: "The LED Display of the corresponding Suture Bin will display the quantity of the box scanned.",
            description: "Remove the existing suture from the Suture Bin.",
            modalInfo: {
                location: LMHelpModalLocation.Right,
                isInteractive: true
            },
            sequenceInfo: {
                type: SequenceType.TapToProgress,
                prefix: "/assets/sequences/SmartShelves_3_SutureReplenish/SmartShelf_Replenish_00",
                start: 215,
                end: 215,
                suffix: 'jpg'
            },
        },
        {
            type: StepType.Sequence,
            title: "Place the entire box of new sutures in the Suture Bin. Do not split the inventory across multiple locations.",
            description: "Place the existing sutures in front of the new sutures to ensure that older products get used first.<br/>If the full box quantity is not replenished in the Suture Bin, the LED Display will display the difference in quantities.<br/>(ex: 12 sutures were expected, 8 sutures were replenished, and the LED Display will display 4)",
            modalInfo: {
                location: LMHelpModalLocation.Bottom,
                isInteractive: false,
                descriptionUsesHTML: true
            },
            sequenceInfo: {
                type: SequenceType.TapToProgress,
                prefix: "/assets/sequences/SmartShelves_3_SutureReplenish/SmartShelf_Replenish_00",
                start: 215,
                end: 380,
                suffix: 'jpg'
            },
            tapLocation: {
                x: 760,
                y: 250
            },
            tappableArea: {
                origin: {
                    x: 580,
                    y: 250
                },
                size: {
                    width: 400,
                    height: 400
                }
            }
        },
        {
            type: StepType.Sequence,
            title: "",
            sequenceInfo: {
                type: SequenceType.Progress,
                prefix: "/assets/sequences/SmartShelves_3_SutureReplenish/SmartShelf_Replenish_00",
                start: 380,
                end: 425,
                suffix: 'jpg'
            }
        },
        {
            type: StepType.Sequence,
            title: "Mark as complete.",
            description: "When finished, select \"Done\" on the Replenishment Scanner.",
            modalInfo: {
                location: LMHelpModalLocation.Left,
                isInteractive: false,
                hideArrow: true
            },
            sequenceInfo: {
                type: SequenceType.TapToProgress,
                prefix: "/assets/sequences/SmartShelves_3_SutureReplenish/SmartShelf_Replenish_00",
                start: 425,
                end: 425,
                suffix: 'jpg'
            },
            tapLocation: {
                x: 1015,
                y: 778
            },
        }
    ],
    name: "Replenishing",
    description: "Replenishing Sutures in the Smart Shelf",
    cardWidth: 400,
    recommendedModules: [TrainingModule.Hub, TrainingModule.Kiosk, TrainingModule.OrderManagement],
    sequenceInfo: {
        type: SequenceType.Progress,
        prefix: "/assets/sequences/SmartShelves_3_SutureReplenish/SmartShelf_Replenish_00",
        start: 0,
        end: 425,
        suffix: 'jpg'
    }
}
export const SmartShelvesSubReplenishingSSScanner: LearningModule = {
    steps: [
        {
            type: StepType.Sequence,
            title: "Smart Shelf screen",
            description: "On the Smart Shelf screen, tap the 3 lines in the upper left corner to open the Smart Shelf’s menu.",
            sequenceInfo: {
                type: SequenceType.TapToProgress,
                prefix: "/assets/sequences/SmartShelves_3_SutureReplenish_SSS/SmartShelf_3_SutureReplenish_ShelfScanner_0000",
                start: 0,
                end: 1,
                suffix: "jpg"
            },
            modalInfo: {
                location: LMHelpModalLocation.Bottom,
                isInteractive: false
            },
            tapLocation: {
                x: 667,
                y: 70
            }
        },
        {
            type: StepType.Sequence,
            title: "Smart Shelf screen",
            description: "Now Select \"Replenish\" from the menu.",
            sequenceInfo: {
                type: SequenceType.TapToProgress,
                prefix: "/assets/sequences/SmartShelves_3_SutureReplenish_SSS/SmartShelf_3_SutureReplenish_ShelfScanner_000",
                start: 1,
                end: 40,
                suffix: "jpg"
            },
            modalInfo: {
                location: LMHelpModalLocation.Bottom,
                isInteractive: false
            },
            tapLocation: {
                x: 740,
                y: 156
            },
            tappableArea: {
                origin: {
                    x: 680,
                    y: 160
                },
                size: {
                    width: 300,
                    height: 100
                }
            }
        },
        {
            type: StepType.Sequence,
            title: "Scan the 2D square barcode on the box of the product to be replenished.",
            description: "If the product box does not have a 2D square barcode, you will need to scan the first 1D barcode on the side of the box and will then be prompted to scan the second 1D barcode.",
            sequenceInfo: {
                type: SequenceType.TapToProgress,
                prefix: "/assets/sequences/SmartShelves_3_SutureReplenish_SSS/SmartShelf_3_SutureReplenish_ShelfScanner_00",
                start: 40,
                end: 117,
                suffix: "jpg"
            },
            modalInfo: {
                location: LMHelpModalLocation.Right,
                isInteractive: false,
                hideArrow: true
            },
            tapLocation: {
                x: 400,
                y: 857
            },
            tappableArea: {
                origin: {
                    x: 434,
                    y: 890
                },
                size: {
                    width: 40,
                    height: 40
                }
            }
        },
        {
            type: StepType.Sequence,
            title: '',
            sequenceInfo: {
                type: SequenceType.Progress,
                prefix: "/assets/sequences/SmartShelves_3_SutureReplenish_SSS/SmartShelf_3_SutureReplenish_ShelfScanner_00",
                start: 117,
                end: 200,
                suffix: 'jpg'
            }
        },
        {
            type: StepType.Sequence,
            title: "The LED Display of the corresponding Suture Bin will display the quantity of the box scanned.",
            description: "Remove the existing suture from the Suture Bin.",
            modalInfo: {
                location: LMHelpModalLocation.Right,
                isInteractive: true
            },
            sequenceInfo: {
                type: SequenceType.TapToProgress,
                prefix: "/assets/sequences/SmartShelves_3_SutureReplenish_SSS/SmartShelf_3_SutureReplenish_ShelfScanner_00",
                start: 200,
                end: 200,
                suffix: 'jpg'
            },
        },
        {
            type: StepType.Sequence,
            title: "Place the entire box of new sutures in the Suture Bin. Do not split the inventory across multiple locations.",
            description: "Place the existing sutures in front of the new sutures to ensure that older products get used first.<br/>If the full box quantity is not replenished in the Suture Bin, the LED Display will display the difference in quantities.<br/>(ex: 12 sutures were expected, 8 sutures were replenished, and the LED Display will display 4)",
            modalInfo: {
                location: LMHelpModalLocation.Bottom,
                isInteractive: false,
                descriptionUsesHTML: true
            },
            sequenceInfo: {
                type: SequenceType.TapToProgress,
                prefix: "/assets/sequences/SmartShelves_3_SutureReplenish_SSS/SmartShelf_3_SutureReplenish_ShelfScanner_00",
                start: 200,
                end: 385,
                suffix: 'jpg'
            },
            tapLocation: {
                x: 760,
                y: 250
            },
            tappableArea: {
                origin: {
                    x: 580,
                    y: 250
                },
                size: {
                    width: 400,
                    height: 400
                }
            }
        },
        {
            type: StepType.Sequence,
            title: "",
            sequenceInfo: {
                type: SequenceType.Progress,
                prefix: "/assets/sequences/SmartShelves_3_SutureReplenish_SSS/SmartShelf_3_SutureReplenish_ShelfScanner_00",
                start: 385,
                end: 409,
                suffix: 'jpg'
            }
        },
        {
            type: StepType.Sequence,
            title: "Mark as complete.",
            description: "When finished, select \"Done\" on the Replenishment Scanner.",
            modalInfo: {
                location: LMHelpModalLocation.Left,
                isInteractive: false,
                hideArrow: true
            },
            sequenceInfo: {
                type: SequenceType.TapToProgress,
                prefix: "/assets/sequences/SmartShelves_3_SutureReplenish_SSS/SmartShelf_3_SutureReplenish_ShelfScanner_00",
                start: 409,
                end: 409,
                suffix: 'jpg'
            },
            tapLocation: {
                x: 1026,
                y: 573
            },
        }
    ],
    name: "Replenishing",
    description: "Replenishing Sutures in the Smart Shelf via the Smart Shelf Scanner",
    cardWidth: 400,
    recommendedModules: [TrainingModule.Hub, TrainingModule.Kiosk, TrainingModule.OrderManagement],
    sequenceInfo: {
        type: SequenceType.Progress,
        prefix: "/assets/sequences/SmartShelves_3_SutureReplenish_SSS/SmartShelf_3_SutureReplenish_ShelfScanner_00",
        start: 0,
        end: 409,
        suffix: 'jpg'
    }
}
export const SmartShelvesTraining: Training = {
    trainingModule: TrainingModule.SmartShelves,
    subModules: [SmartShelvesSubRetrieve, SmartShelvesSubRestocking, SmartShelvesSubReplenishing],
    alternateModules: [SmartShelvesSubReplenishingSSScanner],
    currentSubModuleIndex: 0,
    currentStepIndex: 0,
    howItWorks: [
        "A Smart Shelf may be comprised of 1, 2, or up to a total of 6 racks which are connected to 1 Touchscreen Display and the AITA™ Cloud. The standard Smart Shelf configuration is a set of 3 racks.",
        "Each Suture Bin on a Smart Shelf Rack has an individual weight sensor to track the quantity of sutures. Weight sensors record the weight, and an LED Display in front of a corresponding Suture Bin displays the quantity of sutures in that bin.",
        "When sutures are picked, returned, or replenished, the weight sensor will record the new weight and the LED Display will update with the current inventory level."
    ],
    recommendedModules: [TrainingModule.Hub, TrainingModule.Kiosk, TrainingModule.ScanningSutures, TrainingModule.OrderManagement],
}

export const HubRetrieve: LearningModule = {
    steps: [
        {
            type: StepType.Sequence,
            title: "",
            sequenceInfo: {
                type: SequenceType.Progress,
                prefix: "/assets/sequences/Hub_1_SutureRetrieval/Hub_1_SutureRetrieve_000",
                start: 0,
                end: 60,
                suffix: "jpg"
            }
        },
        {
            type: StepType.Sequence,
            title: "Select the appropriate OR on the Mobile Hub screen.",
            sequenceInfo: {
                type: SequenceType.TapToProgress,
                prefix: "/assets/sequences/Hub_1_SutureRetrieval/Hub_1_SutureRetrieve_00",
                start: 60,
                end: 103,
                suffix: "jpg"
            },
            tapLocation: {
                x: 664,
                y: 391,
            },
            tappableArea: {
                origin: {
                    x: 488,
                    y: 408,
                },
                size: {
                    width: 454,
                    height: 71
                }
            },
            modalInfo: {
                location: LMHelpModalLocation.Bottom,
                isInteractive: false
            }
        },
        {
            type: StepType.Sequence,
            title: "Remove needed sutures ONE AT A TIME.",
            description: "The Infrared Curtain captures the product code and quantity taken for every suture removed in order to assign the suture(s) to the active case in the selected OR and keep track of live inventory levels.",
            sequenceInfo: {
                type: SequenceType.TapToProgress,
                prefix: "/assets/sequences/Hub_1_SutureRetrieval/Hub_1_SutureRetrieve_00",
                start: 103,
                end: 134,
                suffix: "jpg"
            },
            tapLocation: {
                x: 848,
                y: 280
            },
            modalInfo: {
                location: LMHelpModalLocation.Bottom,
                isInteractive: false
            }
        },
        {
            type: StepType.Sequence,
            title: "Remove needed sutures ONE AT A TIME.",
            description: "The Infrared Curtain captures the product code and quantity taken for every suture removed in order to assign the suture(s) to the active case in the selected OR and keep track of live inventory levels.",
            sequenceInfo: {
                type: SequenceType.TapToProgress,
                prefix: "/assets/sequences/Hub_1_SutureRetrieval/Hub_1_SutureRetrieve_00",
                start: 134,
                end: 164,
                suffix: "jpg"
            },
            tapLocation: {
                x: 736,
                y: 280
            },
            modalInfo: {
                location: LMHelpModalLocation.Bottom,
                isInteractive: false
            }
        },
        {
            type: StepType.Sequence,
            title: "Remove needed sutures ONE AT A TIME.",
            description: "The Infrared Curtain captures the product code and quantity taken for every suture removed in order to assign the suture(s) to the active case in the selected OR and keep track of live inventory levels.",
            sequenceInfo: {
                type: SequenceType.TapToProgress,
                prefix: "/assets/sequences/Hub_1_SutureRetrieval/Hub_1_SutureRetrieve_00",
                start: 164,
                end: 212,
                suffix: "jpg"
            },
            tapLocation: {
                x: 1260,
                y: 120
            },
            modalInfo: {
                location: LMHelpModalLocation.Bottom,
                isInteractive: false
            }
        },
        {
            type: StepType.Image,
            title: "When finished picking sutures",
            description: "Select \"Done\" on the Mobile Hub screen to alert the system that picking is complete.<br/>Sutures should still be scanned in the OR during the procedure, once used.",
            imageIsFull: true,
            imagePath: "/assets/sequences/Hub_1_SutureRetrieval/Hub_1_SutureRetrieve_00061.jpg",
            modalInfo: {
                location: LMHelpModalLocation.Bottom,
                isInteractive: false,
                descriptionUsesHTML: true
            },
            tappableArea: {
                origin: {
                    x: 0,
                    y: 0
                },
                size: {
                    width: 1920,
                    height: 1080
                }
            }
        }
    ],
    name: "Retrieving",
    description: "Retrieving Sutures from the Mobile Hub.",
    cardWidth: 379,
    recommendedModules: [TrainingModule.SmartShelves, TrainingModule.Kiosk, TrainingModule.OrderManagement],
    sequenceInfo: {
        type: SequenceType.Progress,
        prefix: "/assets/sequences/Hub_1_SutureRetrieval/Hub_1_SutureRetrieve_00",
        start: 0,
        end: 275,
        suffix: 'jpg'
    }
}
export const HubRestocking: LearningModule = {
    steps: [
        {
            type: StepType.Image,
            title: "Two different methods",
            description: "When returning sutures to the Mobile Hub, you can select between \"Adjust Inventory\" or \"Return\".",
            imageIsFull: true,
            imagePath: "/assets/images/Hub_Adjust_or_Restock.jpg",
            modalInfo: {
                location: LMHelpModalLocation.Bottom,
                isInteractive: false
            },
            tappableArea: {
                origin: {
                    x: 485,
                    y: 740
                },
                size: {
                    width: 946,
                    height: 73
                }
            }
        },
        {
            type: StepType.Inventory,
            title: "Select \"Adjust Inventory\" or \"Return\" then enter some numbers",
            description: "Do this to see how it affects the current number of sutures for a single product",
            modalInfo: {
                location: LMHelpModalLocation.Bottom,
                isInteractive: false
            }
        }
    ],
    name: "Returning",
    description: "Returning Sutures to the Mobile Hub.",
    cardWidth: 379,
    recommendedModules: [TrainingModule.SmartShelves, TrainingModule.Kiosk, TrainingModule.OrderManagement]
}
export const HubReplenishing: LearningModule = {
    steps: [
        {
            type: StepType.Image,
            title: "On the Smart Shelf screen",
            description: "Open the menu by clicking on the 3 horizontal lines in the upper left corner and click \"Request Transfers.\" This ensures that transfers are generated based on real-time inventory levels",
            imagePath: "/assets/images/Replenishment_via_SmartShelf_tranx_1.png",
            imageIsFull: true,
            modalInfo: {
                location: LMHelpModalLocation.Bottom,
                isInteractive: false
            },
            tapLocation: {
                x: 667,
                y: 72
            },
        },
        {
            type: StepType.Image,
            title: "On the Smart Shelf screen",
            description: "Open the menu by clicking on the 3 horizontal lines in the upper left corner and click \"Request Transfers.\" This ensures that transfers are generated based on real-time inventory levels",
            imagePath: "/assets/images/Replenishment_via_SmartShelf_tranx_2.png",
            imageIsFull: true,
            modalInfo: {
                location: LMHelpModalLocation.Bottom,
                isInteractive: false
            },
            tapLocation: {
                x: 760,
                y: 318
            },
            tappableArea: {
                origin: {
                    x: 710,
                    y: 349
                },
                size: {
                    width: 218,
                    height: 47
                }
            }
        },
        {
            type: StepType.Image,
            title: "Select the device that you are replenishing.",
            imagePath: "/assets/images/Replenishment_via_SmartShelf_tranx_3.png",
            imageIsFull: true,
            modalInfo: {
                location: LMHelpModalLocation.Bottom,
                isInteractive: false
            },
            tapLocation: {
                x: 910,
                y: 335
            },
            tappableArea: {
                origin: {
                    x: 740,
                    y: 339
                },
                size: {
                    width: 447,
                    height: 100
                }
            }
        },
        {
            type: StepType.Sequence,
            title: "",
            description: "",
            sequenceInfo: {
                type: SequenceType.Progress,
                prefix: "/assets/sequences/Hub_Replenish_via_SmartShelf/Hub_Replenishment_via_SmartShelf_tranx_000",
                start: 0,
                end: 52,
                suffix: 'jpg'
            }
        },
        {
            type: StepType.Sequence,
            title: "The Smart Shelf LED displays will indicate the quantity of each product code that needs to be picked for transfer.",
            description: "Remove that quantity of inventory from the corresponding Suture Bin. Do not remove the Suture Bin from the Smart Shelf when picking or returning sutures.",
            sequenceInfo: {
                type: SequenceType.TapToProgress,
                start: 52,
                end: 142,
                prefix: "/assets/sequences/Hub_Replenish_via_SmartShelf/Hub_Replenishment_via_SmartShelf_tranx_00",
                suffix: 'jpg'
            },
            tapLocation: {
                x: 860,
                y: 160
            },
            modalInfo: {
                location: LMHelpModalLocation.Bottom,
                isInteractive: false,
            }
        },
        {
            type: StepType.Sequence,
            title: "The Smart Shelf LED displays will indicate the quantity of each product code that needs to be picked for transfer.",
            description: "Remove that quantity of inventory from the corresponding Suture Bin. Do not remove the Suture Bin from the Smart Shelf when picking or returning sutures.",
            sequenceInfo: {
                type: SequenceType.TapToProgress,
                start: 142,
                end: 271,
                prefix: "/assets/sequences/Hub_Replenish_via_SmartShelf/Hub_Replenishment_via_SmartShelf_tranx_00",
                suffix: 'jpg'
            },
            tapLocation: {
                x: 945,
                y: 160
            },
            modalInfo: {
                location: LMHelpModalLocation.Bottom,
                isInteractive: false,
            }
        },
        {
            type: StepType.Sequence,
            title: "The Smart Shelf LED displays will indicate the quantity of each product code that needs to be picked for transfer.",
            description: "Remove that quantity of inventory from the corresponding Suture Bin. Do not remove the Suture Bin from the Smart Shelf when picking or returning sutures.",
            sequenceInfo: {
                type: SequenceType.TapToProgress,
                start: 271,
                end: 390,
                prefix: "/assets/sequences/Hub_Replenish_via_SmartShelf/Hub_Replenishment_via_SmartShelf_tranx_00",
                suffix: 'jpg'
            },
            tapLocation: {
                x: 942,
                y: 480
            },
            modalInfo: {
                location: LMHelpModalLocation.Bottom,
                isInteractive: false,
            }
        },
        {
            type: StepType.Sequence,
            title: "The Smart Shelf LED displays will indicate the quantity of each product code that needs to be picked for transfer.",
            description: "Remove that quantity of inventory from the corresponding Suture Bin. Do not remove the Suture Bin from the Smart Shelf when picking or returning sutures.",
            sequenceInfo: {
                type: SequenceType.TapToProgress,
                start: 390,
                end: 590,
                prefix: "/assets/sequences/Hub_Replenish_via_SmartShelf/Hub_Replenishment_via_SmartShelf_tranx_00",
                suffix: 'jpg'
            },
            tapLocation: {
                x: 1143,
                y: 308
            },
            modalInfo: {
                location: LMHelpModalLocation.Bottom,
                isInteractive: false,
            }
        },
        {
            type: StepType.Sequence,
            title: "",
            description: "",
            sequenceInfo: {
                type: SequenceType.Progress,
                prefix: "/assets/sequences/Hub_Replenish_via_SmartShelf/Hub_Replenishment_via_SmartShelf_tranx_00",
                start: 590,
                end: 645,
                suffix: 'jpg'
            }
        },
        {
            type: StepType.Image,
            title: "When finished picking sutures",
            description: "Select \"Done\" on the Smart Shelf screen to alert the system that picking is complete.",
            imagePath: "/assets/sequences/Hub_Replenish_via_SmartShelf/Hub_Replenishment_via_SmartShelf_tranx_00646.jpg",
            imageIsFull: true,
            modalInfo: {
                location: LMHelpModalLocation.Bottom,
                isInteractive: false
            },
            tapLocation: {
                x: 1020,
                y: 568,
            },
            tappableArea: {
                origin: {
                    x: 990,
                    y: 580,
                },
                size: {
                    width: 180,
                    height: 80
                }
            }
        },
        {
            type: StepType.Sequence,
            title: "At the Mobile Hub, pull out the Suture Cartridge.",
            description: "You will need to locate the Suture Cartridge location that corresponds with the suture you’re transferring.",
            modalInfo: {
                location: LMHelpModalLocation.Bottom,
                isInteractive: false
            },
            sequenceInfo: {
                type: SequenceType.Draggable,
                prefix: "/assets/sequences/Hub_Replenish_via_SmartShelf/Hub_Replenishment_via_SmartShelf_tranx_00",
                start: 647,
                end: 710,
                suffix: "jpg",
                dragDirection: DragDirection.RightToLeft
            },
            dragInfo: {
                origin: {
                    x: 920,
                    y: 492
                },
                rotation: -110,
                scale: 1.5
            }
        },
        {
            type: StepType.Image,
            title: "Confirm the correct product code is housed in this location.",
            imagePath: "/assets/sequences/Hub_Replenish_via_SmartShelf/Hub_Replenishment_via_SmartShelf_tranx_00710.jpg",
            imageIsFull: true,
            modalInfo: {
                location: LMHelpModalLocation.Right,
                isInteractive: true,
                
            },
            tappableArea: {
                origin: {
                    x: 0,
                    y: 0,
                },
                size: {
                    width: 1920,
                    height: 1080
                }
            },
            showTappableArea: true
        },
        {
            type: StepType.Sequence,
            title: "Drag down to place the transferred sutures in the Suture Cartridge.",
            description: "BEST PRACTICE: Count the total number of sutures that will be in the Suture Cartridge after transfer before you add the new sutures (existing sutures + transferred sutures).</br>Place the transferred sutures on top of the existing sutures. This will ensure that older product gets used first. Ensure all sutures have the same orientation with bar codes facing up.",
            modalInfo: {
                location: LMHelpModalLocation.Bottom,
                isInteractive: false,
                descriptionUsesHTML: true
            },
            sequenceInfo: {
                type: SequenceType.Draggable,
                prefix: "/assets/sequences/Hub_Replenish_via_SmartShelf/Hub_Replenishment_via_SmartShelf_tranx_00",
                start: 710,
                end: 799,
                suffix: "jpg",
                dragDirection: DragDirection.TopToBottom
            },
            dragInfo: {
                origin: {
                    x: 870,
                    y: 50
                },
                rotation: -180,
                scale: 1.5
            }
        },
        {
            type: StepType.Sequence,
            title: "Push the Suture Cartridge back into the Mobile Hub until it magnetizes into place.",
            modalInfo: {
                location: LMHelpModalLocation.Bottom,
                isInteractive: false
            },
            sequenceInfo: {
                type: SequenceType.Draggable,
                prefix: "/assets/sequences/Hub_Replenish_via_SmartShelf/Hub_Replenishment_via_SmartShelf_tranx_00",
                start: 799,
                end: 859,
                suffix: "jpg",
                dragDirection: DragDirection.LeftToRight
            },
            dragInfo: {
                origin: {
                    x: 500,
                    y: 442
                },
                rotation: 80,
                scale: 1.5
            },
        },
        {
            type: StepType.Image,
            title: "Go to the Mobile Hub screen and select \"Adjust Inventory\".",
            imagePath: "/assets/images/Hub_Adjust_only.png",
            imageIsFull: true,
            modalInfo: {
                location: LMHelpModalLocation.Bottom,
                isInteractive: true
            },
            tappableArea: {
                origin: {
                    x: 485,
                    y: 740
                },
                size: {
                    width: 946,
                    height: 73
                }
            }
        },
        {
            type: StepType.Image,
            title: "Select the corresponding suture cartridge location for the sutures you just replenished via transfer.",
            imagePath: "/assets/images/Hub_Inventory.png",
            imageIsFull: true,
            modalInfo: {
                location: LMHelpModalLocation.Bottom,
                isInteractive: true
            },
            tapLocation: {
                x: 856,
                y: 400
            }
        },
        {
            type: StepType.Image,
            title: "Enter the new TOTAL suture quantity in the replenished suture cartridge.",
            description: "This can be done by using the \"+\" or \"-\" buttons or by typing in the total quantity.",
            imagePath: "/assets/images/Hub_Adj_Inventory_9.png",
            imageIsFull: true,
            modalInfo: {
                location: LMHelpModalLocation.Bottom,
                isInteractive: true
            },
            tapLocation: {
                x: 913,
                y: 634
            }
        },
        {
            type: StepType.Image,
            title: "When finished, click the checkmark to return to the main Adjust Inventory screen.",
            imagePath: "/assets/images/Hub_Adj_Inventory_10.png",
            imageIsFull: true,
            modalInfo: {
                location: LMHelpModalLocation.Bottom,
                isInteractive: true
            },
            tapLocation: {
                x: 1172,
                y: 575
            }
        },
        {
            type: StepType.Image,
            title: "When finished with all suture transfers, click the \"X\" in the upper right corner.",
            imagePath: "/assets/images/Hub_Inventory.png",
            imageIsFull: true,
            modalInfo: {
                location: LMHelpModalLocation.Bottom,
                isInteractive: true
            },
            tapLocation: {
                x: 1365,
                y: 206
            }
        }
    ],
    name: "Transfer",
    description: "Transfer Sutures to the Mobile Hub.",
    cardWidth: 379,
    recommendedModules: [TrainingModule.SmartShelves, TrainingModule.Kiosk, TrainingModule.OrderManagement, TrainingModule.ExpiryManagement],
    sequenceInfo: {
        type: SequenceType.Progress,
        prefix: "/assets/sequences/Hub_Replenish_via_SmartShelf/Hub_Replenishment_via_SmartShelf_tranx_0",
        start: 0,
        end: 1104,
        suffix: 'jpg'
    }
}
export const HubTraining: Training = {
    trainingModule: TrainingModule.Hub,
    subModules: [HubRetrieve, HubReplenishing],
    currentSubModuleIndex: 0,
    currentStepIndex: 0,
    howItWorks: [
        "The Mobile Hub is equipped with an Infrared (IR) Array Frame, also known as an Infrared Curtain.",
        "The curtain creates a pane of IR beams that are monitored for interruption.",
        "When a suture needs to be retrieved, the user will indicate which OR they are retrieving for on the Mobile Hub screen.",
        "Once a suture is picked, the curtain detects an interruption, and a suture removal is signaled. The suture removed is then recorded to the AITA™ Cloud for that specific procedure.",
        "Ensure the Mobile Hub remains PLUGGED IN so as not to appear “OFFLINE” (Mobile Hub has its own power cord)."
    ],
    recommendedModules: [TrainingModule.Kiosk, TrainingModule.SmartShelves, TrainingModule.ScanningSutures, TrainingModule.OrderManagement],
    note: "Note: based on your AITA Hardware configuration, all unused inventory should be returned to the Kiosk, the Smart Shelf, or the central location for suture returns as determined by your inventory management team."
}

export const ScanTraining: LearningModule = {
    steps: [
        {
            type: StepType.Sequence,
            title: "",
            sequenceInfo: {
                type: SequenceType.Progress,
                prefix: "/assets/sequences/ScanningSutures/ScanningSutures_000",
                start: 0,
                end: 90,
                suffix: "jpg"
            }
        },
        {
            type: StepType.Sequence,
            title: "Select the QR code on the AITA™ Pick Sheet to scan it.",
            description: "The scanner will beep once the AITA™ Pick Sheet is successfully scanned. Note: Add-On Cases will not have an AITA™ Pick Sheet.",
            sequenceInfo: {
                type: SequenceType.TapToProgress,
                prefix: "/assets/sequences/ScanningSutures/ScanningSutures_00",
                start: 90,
                end: 255,
                suffix: "jpg"
            },
            modalInfo: {
                location: LMHelpModalLocation.Right,
                isInteractive: false,
                hideArrow: true
            },
            tapLocation: {
                x: 475,
                y: 820,
            },
            tappableArea: {
                origin: {
                    x: 415,
                    y: 755,
                },
                size: {
                    width: 228,
                    height: 250
                }
            }
        },
        {
            type: StepType.Sequence,
            title: "After opening a suture, scan the QR code on the suture package.",
            description: "The scanner will beep once the suture is successfully scanned. Repeat this process for all used sutures.",
            sequenceInfo: {
                type: SequenceType.TapToProgress,
                prefix: "/assets/sequences/ScanningSutures/ScanningSutures_00",
                start: 255,
                end: 365,
                suffix: "jpg"
            },
            modalInfo: {
                location: LMHelpModalLocation.Bottom,
                isInteractive: false
            },
            tapLocation: {
                x: 373,
                y: 473,
            },
            tappableArea: {
                origin: {
                    x: 401,
                    y: 503,
                },
                size: {
                    width: 50,
                    height: 50
                }
            }
        },
        {
            type: StepType.Sequence,
            title: "After opening a suture, scan the QR code on the suture package.",
            description: "The scanner will beep once the suture is successfully scanned. Repeat this process for all used sutures.",
            sequenceInfo: {
                type: SequenceType.TapToProgress,
                prefix: "/assets/sequences/ScanningSutures/ScanningSutures_00",
                start: 365,
                end: 476,
                suffix: "jpg"
            },
            modalInfo: {
                location: LMHelpModalLocation.Bottom,
                isInteractive: false
            },
            tapLocation: {
                x: 400,
                y: 473,
            },
            tappableArea: {
                origin: {
                    x: 425,
                    y: 503,
                },
                size: {
                    width: 50,
                    height: 50
                }
            }
        },
        {
            type: StepType.Sequence,
            title: "After the case, scan the QR code on AITA™ Pick Sheet to close out the case.",
            description: "Note: Add-On Cases will not have an AITA™ Pick Sheet.",
            sequenceInfo: {
                type: SequenceType.TapToProgress,
                prefix: "/assets/sequences/ScanningSutures/ScanningSutures_00",
                start: 476,
                end: 586,
                suffix: "jpg"
            },
            modalInfo: {
                location: LMHelpModalLocation.Right,
                isInteractive: false,
                hideArrow: true
            },
            tapLocation: {
                x: 475,
                y: 820,
            },
            tappableArea: {
                origin: {
                    x: 415,
                    y: 755,
                },
                size: {
                    width: 228,
                    height: 250
                }
            }
        },
        {
            type: StepType.Image,
            title: "Unused sutures",
            description: "Based on your AITA Hardware configuration, all unused sutures should be returned to the Kiosk, the Smart Shelf, or the central location for suture returns as determined by your inventory management team.",
            imageIsFull: true,
            imagePath: "/assets/images/BinStack.png",
            modalInfo: {
                location: LMHelpModalLocation.Right,
                isInteractive: true
            }
        }
    ],
    name: "Scanning Sutures",
    description: "Scanning Sutures",
    cardWidth: 333,
    recommendedModules: [TrainingModule.Kiosk, TrainingModule.SmartShelves, TrainingModule.Hub, TrainingModule.OrderManagement],
    suggestedNextSteps: [KioskSubRestocking, HubRetrieve],
    sequenceInfo: {
        type: SequenceType.Progress,
        prefix: "/assets/sequences/ScanningSutures/ScanningSutures_00",
        start: 0,
        end: 586,
        suffix: 'jpg'
    }
}
export const ScanningTraining: Training = {
    trainingModule: TrainingModule.ScanningSutures,
    subModules: [ScanTraining],
    currentSubModuleIndex: 0,
    currentStepIndex: 0,
    recommendedModules: [TrainingModule.Kiosk, TrainingModule.SmartShelves, TrainingModule.Hub, TrainingModule.OrderManagement]
}

export const OrderManagementModule: LearningModule = {
    steps: [
        {
            type: StepType.Image,
            title: "Inventory levels",
            description: "When inventory levels reach a predetermined par level, the Materials Manager will receive recommendations to reorder sutures.",
            imageIsCenter: true,
            imagePath: "/assets/images/OM_inventory_levels.png",
            modalInfo: {
                location: LMHelpModalLocation.Center,
                isInteractive: true
            }
        },
        {
            type: StepType.Image,
            title: "Log into the AITA™ Engine and navigate to the cart under the Operations & Insights tab.",
            imageIsCenter: true,
            imagePath: "/assets/images/OM_sign_in.png",
            modalInfo: {
                location: LMHelpModalLocation.Bottom,
                isInteractive: false
            },
            tapLocation: {
                x: 1044,
                y: 755
            }
        },
        {
            type: StepType.Image,
            title: "Cart",
            description: `
            <ul class='modal-list'>
                <li><b>Every time you enter the cart, it will refresh and provide up-to-date order recommendations</b></li>
                <li>The cart will automatically close out after 15 minutes</li>
                <li>Only one user can access the cart at a time</li>
                <li>The cart will be populated with suggested quantities based on predetermined par levels</li>
                <li>After 90 days of using the system, the cart will be populated with suggested quantities based on average daily usage collected by AITA™</li>
            </ul>`,
            imageIsCenter: true,
            imagePath: "/assets/images/OM_cart.png",
            modalInfo: {
                location: LMHelpModalLocation.Center,
                isInteractive: true,
                descriptionUsesHTML: true
            }
        },
        {
            type: StepType.Image,
            title: "Use on-screen tools to modify recommendations",
            description: "By default, only low inventory levels are in the cart. Items can be removed from cart or medium inventory level items can be added. If a product needs to be ordered that’s not on either list, click \"Add to Cart\" and search for the product code.",
            imageIsCenter: true,
            imagePath: "/assets/images/OM_cart.png",
            modalInfo: {
                location: LMHelpModalLocation.Bottom,
                isInteractive: false
            },
            tapLocation: {
                x: 1165,
                y: 394
            }
        },
        {
            type: StepType.Image,
            title: "Place your order",
            description: "If AITA™ is integrated into your hospital’s ordering system, selecting \"Submit Order\" will place the order. If not, use the \"Print Cart\" option to print the cart and reference it when working in your ordering system.",
            imageIsCenter: true,
            imagePath: "/assets/images/OM_cart_2.png",
            modalInfo: {
                location: LMHelpModalLocation.Bottom,
                isInteractive: false
            },
            tapLocation: {
                x: 1360,
                y: 660
            }
        },
        {
            type: StepType.Image,
            title: "Accessing the cart after order submission",
            description: "After submitting the order, the system will prompt you to enter the order or PO number from your hospital’s ordering system. This is required before you can go back into the cart.",
            imageIsCenter: true,
            imagePath: "/assets/images/OM_cart_Lookup.png",
            modalInfo: {
                location: LMHelpModalLocation.Bottom,
                isInteractive: false
            },
            tappableArea: {
                origin: {
                    x: 0,
                    y: 0
                },
                size: {
                    width: 1920,
                    height: 1080
                }
            }
        }
    ],
    name: "Order Management",
    description: "Order Management",
    cardWidth: 333,
    recommendedModules: [TrainingModule.SmartShelves, TrainingModule.Hub, TrainingModule.Kiosk],
    suggestedNextSteps: [ScanTraining, HubRetrieve],
}
export const OrderManagementTraining: Training = {
    trainingModule: TrainingModule.OrderManagement,
    subModules: [OrderManagementModule],
    currentSubModuleIndex: 0,
    currentStepIndex: 0,
    recommendedModules: [TrainingModule.Kiosk, TrainingModule.SmartShelves, TrainingModule.Hub, TrainingModule.ScanningSutures]
}

export const ExpiryManagementModule: LearningModule = {
    steps: [
        {
            type: StepType.Image,
            title: "Expiry Management",
            description: "Please note, the Expiry Management process should be completed monthly as a best practice.",
            imageIsCenter: true,
            imagePath: "/assets/images/update_removed_batches_5.png",
            modalInfo: {
                location: LMHelpModalLocation.Center,
                isInteractive: true
            }
        },
        {
            type: StepType.Image,
            title: "Log into the AITA™ Engine and navigate to Expiring Sutures by Location under the Operations & Insights tab.",
            imageIsCenter: true,
            imagePath: "/assets/images/OM_sign_in.png",
            modalInfo: {
                location: LMHelpModalLocation.Bottom,
                isInteractive: false
            },
            tapLocation: {
                x: 1044,
                y: 755
            }
        },
        {
            type: StepType.Image,
            title: "Print the Expiring Sutures by Location page.",
            description: "This will act as a tracker as you find and remove expiring suture. Make note of the number of sutures you remove by location",
            imageIsCenter: true,
            imagePath: "/assets/images/update_removed_batches_5.png",
            modalInfo: {
                location: LMHelpModalLocation.Bottom,
                isInteractive: true
            },
            tapLocation: {
                x: 1355,
                y: 252
            }
        },
        {
            type: StepType.Image,
            title: "Once you are finished finding and removing expiring sutures and updating your tracker",
            description: "You will go back to the Expiring Sutures by Location page on the AITA™ Engine and either type in the number of expiring sutures you found and removed in the corresponding device and click \"Remove\" or indicate that no expiring sutures were found in the corresponding device by clicking \"None Found.\"",
            imageIsCenter: true,
            imagePath: "/assets/images/update_removed_batches_5.png",
            modalInfo: {
                location: LMHelpModalLocation.Center,
                isInteractive: true
            }
        },
        {
            type: StepType.Image,
            title: "Tap indicated location to enter a quantity of \"2\" to be removed",
            imageIsCenter: true,
            imagePath: "/assets/images/update_removed_batches_5.png",
            modalInfo: {
                location: LMHelpModalLocation.Bottom,
                isInteractive: false
            },
            tapLocation: {
                x: 1089,
                y: 430
            }
        },
        {
            type: StepType.Image,
            title: "Tap remove to confirm.",
            imageIsCenter: true,
            imagePath: "/assets/images/update_removed_batches_5_2.jpg",
            modalInfo: {
                location: LMHelpModalLocation.Bottom,
                isInteractive: false
            },
            tapLocation: {
                x: 1223,
                y: 433
            }
        },
        {
            type: StepType.Image,
            title: "As expiring sutures are removed from each device, the Estimated System Quantity for the corresponding product code in the other device locations will update accordingly.",
            imageIsCenter: true,
            imagePath: "/assets/images/update_removed_batches_3.png",
            modalInfo: {
                location: LMHelpModalLocation.Bottom,
                isInteractive: true
            },
            tapLocation: {
                x: 886,
                y: 674
            }
        },
        {
            type: StepType.Image,
            title: "Users can also view expiring sutures by product code by navigating to Expiring Sutures by Product under the Operations & Insights tab.",
            description: "This function is helpful when you find an expiring suture in a device and want to see where else that expiring suture code is located.",
            imageIsCenter: true,
            imagePath: "/assets/images/expiring_by_product.png",
            modalInfo: {
                location: LMHelpModalLocation.Center,
                isInteractive: true
            }
        },
        {
            type: StepType.Image,
            title: "Select a product code then select \"Location Details.\"",
            imageIsCenter: true,
            imagePath: "/assets/images/expiring_by_product.png",
            modalInfo: {
                location: LMHelpModalLocation.Bottom,
                isInteractive: false
            },
            tapLocation: {
                x: 427,
                y: 401
            }
        },
        {
            type: StepType.Image,
            title: "Select a product code then select \"Location Details.\"",
            imageIsCenter: true,
            imagePath: "/assets/images/expiring_by_product_selected.jpg",
            modalInfo: {
                location: LMHelpModalLocation.Bottom,
                isInteractive: false
            },
            tapLocation: {
                x: 1330,
                y: 644
            },
            tappableArea: {
                origin: {
                    x: 1315,
                    y: 680
                },
                size: {
                    width: 145,
                    height: 37
                }
            }
        },
        {
            type: StepType.Image,
            title: "Print the Expiring Sutures by Product page",
            description: "This will act as a tracker as you find and remove expiring suture. Make note of the number of sutures you remove by location.",
            imageIsCenter: true,
            imagePath: "/assets/images/Expiring.png",
            modalInfo: {
                location: LMHelpModalLocation.Bottom,
                isInteractive: false
            },
            tapLocation: {
                x: 1384,
                y: 394
            }
        },
        {
            type: StepType.Image,
            title: "Once you are finished finding and removing expiring sutures and updating your tracker",
            description: "You will go back to the Expiring Sutures by Product page on the AITA™ Engine, select the corresponding product code, and either type in the number of expiring sutures you found and removed in the corresponding device and click \"Remove\" or indicate that no expiring sutures were found in the corresponding device by clicking \"None Found.\"",
            imageIsCenter: true,
            imagePath: "/assets/images/Expiring.png",
            modalInfo: {
                location: LMHelpModalLocation.Center,
                isInteractive: true
            }
        },
        {
            type: StepType.Image,
            title: "Tap indicated location to enter a quantity of \"2\" to be removed",
            imageIsCenter: true,
            imagePath: "/assets/images/Expiring.png",
            modalInfo: {
                location: LMHelpModalLocation.Bottom,
                isInteractive: false
            },
            tapLocation: {
                x: 1047,
                y: 465
            },
            tappableArea: {
                origin: {
                    x: 1040,
                    y: 502
                },
                size: {
                    width: 127,
                    height: 30
                }
            }
        },
        {
            type: StepType.Image,
            title: "Tap remove to confirm.",
            imageIsCenter: true,
            imagePath: "/assets/images/Expiring.png",
            modalInfo: {
                location: LMHelpModalLocation.Bottom,
                isInteractive: false
            },
            tapLocation: {
                x: 1196,
                y: 465
            },
            tappableArea: {
                origin: {
                    x: 1206,
                    y: 502
                },
                size: {
                    width: 90,
                    height: 31
                }
            }
        },
    ],
    name: "Expiry Management",
    description: "Expiry Management",
    cardWidth: 333,
    recommendedModules: [TrainingModule.SmartShelves, TrainingModule.Hub, TrainingModule.Kiosk],
    suggestedNextSteps: [ScanTraining, HubRetrieve],
}
export const ExpiryManagementTraining: Training = {
    trainingModule: TrainingModule.ExpiryManagement,
    subModules: [ExpiryManagementModule],
    currentSubModuleIndex: 0,
    currentStepIndex: 0,
    recommendedModules: [TrainingModule.Kiosk, TrainingModule.SmartShelves, TrainingModule.Hub, TrainingModule.ScanningSutures]
}

export const TrainingForUser: UserTraining[] = [
    {
        role: UserRole.Clinical,
        required: [TrainingModule.Hub, TrainingModule.ScanningSutures],
        optional: [TrainingModule.Kiosk, TrainingModule.SmartShelves]
    },
    {
        role: UserRole.Material,
        required: [TrainingModule.Kiosk, TrainingModule.SmartShelves, TrainingModule.Hub, TrainingModule.OrderManagement, TrainingModule.ExpiryManagement],
        optional: [TrainingModule.ScanningSutures]
    }
]

export function getTrainingForUserRole(userRole: UserRole): UserTraining {
    return TrainingForUser.find((value: UserTraining) => {return value.role === userRole}) ?? TrainingForUser[0]
}